import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom";
import "./RepairsPage.css";
import { getAuthToken } from "./auth";

// =====================================================
// API CONFIG
// =====================================================

const CLOUD_API_URL = process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

const LOCAL_API_URL =
  process.env.REACT_APP_LOCAL_API_URL || "http://localhost:7000";

/*
  Admin website:
    admin.deskpos.ie
      -> cloud API
      -> JWT authentication

  POS / Electron:
    localhost / Electron
      -> localhost:7000
      -> till-local/server.js
      -> cloud using till authentication
*/
const IS_ADMIN_DOMAIN = window.location.hostname === "admin.deskpos.ie";

const REPAIR_API_URL = IS_ADMIN_DOMAIN ? CLOUD_API_URL : LOCAL_API_URL;

// =====================================================
// STATUS OPTIONS
// =====================================================

const STATUS_OPTIONS = [
    {
    value: "received",
    label: "Received from Customer",
  },
  {
    value: "diagnosing",
    label: "Diagnosing",
  },
  {
    value: "waiting_customer",
    label: "Waiting for Customer",
  },
  {
    value: "parts_ordered",
    label: "Parts Ordered",
  },
  {
    value: "repair_in_progress",
    label: "Repair in Progress",
  },
  {
    value: "ready_for_collection",
    label: "Ready for Collection",
  },
   {
    value: "collected",
    label: "Collected by Customer",
  },
  {
    value: "cancelled",
    label: "Cancelled",
  },
  {
    value: "cannot_repair",
    label: "Cannot Repair",
  },
];

// =====================================================
// EMPTY NEW REPAIR FORM
// =====================================================

const EMPTY_FORM = {
  customer_name: "",
  customer_phone: "",
  device_name: "",
  imei: "",
  issue: "",
  total_amount: "",
  amount_paid: "",
  notes: "",
};

// =====================================================
// AUTH CONFIG
// =====================================================

const getRepairRequestConfig = () => {
  /*
    POS / Electron does NOT send JWT.

    It talks to localhost:7000.
    The local server will send:

      X-Till-Id
      X-Till-Token
      X-User-Id

    to the cloud.
  */
  if (!IS_ADMIN_DOMAIN) {
    return {};
  }

  /*
    Admin website still uses normal DeskPOS JWT.
  */
  const token = String(
    getAuthToken() ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      "",
  ).trim();

  if (!token) {
    return null;
  }

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

// =====================================================
// API ERROR MESSAGE
// =====================================================

const getRepairRequestErrorMessage = (err, fallback) => {
  if (err.response?.status === 401) {
    if (IS_ADMIN_DOMAIN) {
      return "Your admin login session has expired. Please log in again.";
    }

    return (
      err.response?.data?.error ||
      "The till could not authenticate with the cloud. " +
        "Check that this till is activated and connected."
    );
  }

  if (err.response?.status === 403) {
    return (
      err.response?.data?.error ||
      "You do not have permission to access this repair."
    );
  }

  return err.response?.data?.error || fallback;
};

// =====================================================
// PAGE
// =====================================================

function RepairsPage() {
  const navigate = useNavigate();

  const searchRef = useRef(null);

  const [repairs, setRepairs] = useState([]);

  const [selectedRepair, setSelectedRepair] = useState(null);

  const [repairHistory, setRepairHistory] = useState([]);

  const [search, setSearch] = useState("");

  const [showNewRepair, setShowNewRepair] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  const [message, setMessage] = useState("");

  // =====================================================
  // PAYMENT MODAL
  // =====================================================

  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [paymentTotal, setPaymentTotal] = useState("");

  const [paymentPaid, setPaymentPaid] = useState("");
  // =====================================================
  // REQUIRE REQUEST CONFIG
  // =====================================================

  const requireRequestConfig = () => {
    const config = getRepairRequestConfig();

    if (IS_ADMIN_DOMAIN && !config) {
      setError("Your admin login session has expired. Please log in again.");

      return null;
    }

    return config || {};
  };

  // =====================================================
  // FORMAT MONEY
  // =====================================================

  const formatMoney = (value) => `€${Number(value || 0).toFixed(2)}`;

  // =====================================================
  // FORMAT STATUS
  // =====================================================

  const formatStatus = (status) => {
    const option = STATUS_OPTIONS.find((item) => item.value === status);

    return option?.label || status || "-";
  };

  // =====================================================
  // LOAD REPAIRS
  // =====================================================

  const loadRepairs = useCallback(async (searchValue = "") => {
    try {
      setLoading(true);
      setError("");

      const requestConfig = getRepairRequestConfig();

      /*
            Only Admin requires JWT here.

            POS gets authentication from
            till-local/server.js instead.
          */
      if (IS_ADMIN_DOMAIN && !requestConfig) {
        setRepairs([]);

        setError("Your admin login session has expired. Please log in again.");

        return;
      }

      const response = await axios.get(`${REPAIR_API_URL}/repairs`, {
        params: {
          search: String(searchValue || "").trim() || undefined,
        },

        ...(requestConfig || {}),
      });

      const list = Array.isArray(response.data?.repairs)
        ? response.data.repairs
        : [];

      setRepairs(list);
    } catch (err) {
      console.error("Load repairs error:", err.response?.data || err.message);

      setError(getRepairRequestErrorMessage(err, "Failed to load repairs"));
    } finally {
      setLoading(false);
    }
  }, []);

  // =====================================================
  // OPEN SINGLE REPAIR
  // =====================================================

  const openRepair = async (repairCode) => {
    try {
      setLoading(true);
      setError("");
      setMessage("");

      const requestConfig = requireRequestConfig();

      if (!requestConfig) {
        return;
      }

      const response = await axios.get(
        `${REPAIR_API_URL}/repairs/${encodeURIComponent(repairCode)}`,
        requestConfig,
      );

      setSelectedRepair(response.data?.repair || null);

      setRepairHistory(
        Array.isArray(response.data?.history) ? response.data.history : [],
      );
    } catch (err) {
      console.error("Open repair error:", err.response?.data || err.message);

      setError(getRepairRequestErrorMessage(err, "Repair not found"));
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    loadRepairs();

    const timer = setTimeout(() => {
      searchRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [loadRepairs]);

  // =====================================================
  // SEARCH / BARCODE SCAN
  // =====================================================

  const handleSearchSubmit = async (event) => {
    event.preventDefault();

    const value = String(search || "").trim();

    if (!value) {
      await loadRepairs();
      return;
    }

    /*
        Barcode example:

        R-000001

        Scanner normally types the value
        followed by Enter.
      */
    if (/^R-\d+$/i.test(value)) {
      await openRepair(value.toUpperCase());

      setSearch("");

      return;
    }

    await loadRepairs(value);
  };

  // =====================================================
  // CREATE REPAIR
  // =====================================================

  const createRepair = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const customerName = form.customer_name.trim();

      const deviceName = form.device_name.trim();

      if (!customerName) {
        setError("Customer name is required");

        return;
      }

      if (!deviceName) {
        setError("Device / model is required");

        return;
      }

      const totalAmount = Number(form.total_amount || 0);

      const amountPaid = Number(form.amount_paid || 0);

      if (!Number.isFinite(totalAmount) || totalAmount < 0) {
        setError("Please enter a valid repair price");

        return;
      }

      if (!Number.isFinite(amountPaid) || amountPaid < 0) {
        setError("Please enter a valid amount paid");

        return;
      }

      if (amountPaid > totalAmount) {
        setError("Amount paid cannot be more than repair price");

        return;
      }

      const payload = {
        customer_name: customerName,

        customer_phone: form.customer_phone.trim(),

        device_name: deviceName,

        imei: form.imei.trim(),

        issue: form.issue.trim(),

        total_amount: totalAmount,

        amount_paid: amountPaid,

        notes: form.notes.trim(),
      };

      const requestConfig = requireRequestConfig();

      if (!requestConfig) {
        return;
      }

      const response = await axios.post(
        `${REPAIR_API_URL}/repairs`,
        payload,
        requestConfig,
      );

      const repair = response.data?.repair;

      setMessage(
        repair?.repair_code
          ? `Repair ${repair.repair_code} created successfully`
          : "Repair created successfully",
      );

      setShowNewRepair(false);

      setForm(EMPTY_FORM);

      await loadRepairs();

      if (repair?.repair_code) {
        await openRepair(repair.repair_code);
      }
    } catch (err) {
      console.error("Create repair error:", err.response?.data || err.message);

      setError(getRepairRequestErrorMessage(err, "Failed to create repair"));
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // CHANGE REPAIR STATUS
  // =====================================================

  const changeStatus = async (newStatus) => {
    if (!selectedRepair?.repair_code) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const requestConfig = requireRequestConfig();

      if (!requestConfig) {
        return;
      }

      await axios.put(
        `${REPAIR_API_URL}/repairs/${encodeURIComponent(
          selectedRepair.repair_code,
        )}/status`,
        {
          status: newStatus,

          note: `Status changed to ${formatStatus(newStatus)}`,
        },
        requestConfig,
      );

      setMessage("Repair status updated");

      await openRepair(selectedRepair.repair_code);

      await loadRepairs();
    } catch (err) {
      console.error(
        "Change repair status error:",
        err.response?.data || err.message,
      );

      setError(getRepairRequestErrorMessage(err, "Failed to update status"));
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // UPDATE PAYMENT
  // =====================================================

  // =====================================================
  // UPDATE PAYMENT
  // =====================================================

  const openPaymentModal = () => {
    if (!selectedRepair?.repair_code) {
      return;
    }

      if (
    selectedRepair.status ===
    "collected"
  ) {
    setError(
      "Payment cannot be changed after the repair has been collected."
    );

    return;
  }

    setError("");
    setMessage("");

    setPaymentTotal(Number(selectedRepair.total_amount || 0).toFixed(2));

    setPaymentPaid(Number(selectedRepair.amount_paid || 0).toFixed(2));

    setShowPaymentModal(true);
  };

  const savePaymentUpdate = async () => {
    if (!selectedRepair?.repair_code) {
      return;
    }

    const totalAmount = Number(paymentTotal);

    const amountPaid = Number(paymentPaid);

    if (!Number.isFinite(totalAmount) || totalAmount < 0) {
      setError("Please enter a valid repair total");

      return;
    }

    if (!Number.isFinite(amountPaid) || amountPaid < 0) {
      setError("Please enter a valid amount paid");

      return;
    }

    if (amountPaid > totalAmount) {
      setError("Amount paid cannot be more than repair total");

      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const requestConfig = requireRequestConfig();

      if (!requestConfig) {
        return;
      }

      await axios.put(
        `${REPAIR_API_URL}/repairs/${encodeURIComponent(
          selectedRepair.repair_code,
        )}/payment`,
        {
          total_amount: totalAmount,
          amount_paid: amountPaid,
        },
        requestConfig,
      );

      setShowPaymentModal(false);

      setMessage("Repair payment updated successfully");

      await openRepair(selectedRepair.repair_code);

      await loadRepairs();
    } catch (err) {
      console.error(
        "Update repair payment error:",
        err.response?.data || err.message,
      );

      setError(getRepairRequestErrorMessage(err, "Failed to update payment"));
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // STATUS COUNTS
  // =====================================================

  const statusCounts = repairs.reduce((counts, repair) => {
    const status = repair.status || "unknown";

    counts[status] = (counts[status] || 0) + 1;

    return counts;
  }, {});

  // Receipt Print Function for Repair Management

  const printRepairReceipt = (repair) => {
    if (!repair) {
      setError("No repair selected");
      return;
    }

    // ==========================================
    // LOAD SHOP SETTINGS
    // ==========================================

    let cachedConfig = {};

    try {
      const saved = localStorage.getItem("shop_config_cache");

      cachedConfig = saved ? JSON.parse(saved) : {};
    } catch {
      cachedConfig = {};
    }

    const shopName =
      cachedConfig.shop_name ||
      cachedConfig.shopName ||
      cachedConfig.receipt_name ||
      cachedConfig.receiptName ||
      "DeskPOS";

    const address =
      cachedConfig.address ||
      cachedConfig.shop_address ||
      cachedConfig.shopAddress ||
      "";

    const phone =
      cachedConfig.phone ||
      cachedConfig.shop_phone ||
      cachedConfig.shopPhone ||
      "";

    const printerName =
      cachedConfig.printer_name ||
      cachedConfig.printerName ||
      "Tysso Thermal Receipt Printer";

    // ==========================================
    // REPAIR DETAILS
    // ==========================================

    const repairCode = repair.repair_code || repair.repairCode || "-";

    const customerName = repair.customer_name || repair.customerName || "-";

    const customerPhone = repair.customer_phone || repair.customerPhone || "-";

    const deviceName = repair.device_name || repair.deviceName || "-";

    const imei = repair.imei || "-";

    const issue = repair.issue || "-";

    const notes = repair.notes || "";

    const receivedBy = repair.received_by_name || repair.receivedByName || "-";

    const totalAmount = Number(repair.total_amount || repair.totalAmount || 0);

    const amountPaid = Number(repair.amount_paid || repair.amountPaid || 0);

    const balance =
      repair.balance !== undefined && repair.balance !== null
        ? Number(repair.balance)
        : Math.max(0, totalAmount - amountPaid);

    const repairDate =
      repair.created_at || repair.createdAt || new Date().toISOString();

    const formattedDate = new Date(repairDate).toLocaleString("en-IE");

    // ==========================================
    // HELPERS
    // ==========================================

    const wrapRepairText = (text, maxChars = 32) => {
      const words = String(text || "").split(" ");

      const lines = [];

      let currentLine = "";

      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;

        if (testLine.length > maxChars) {
          if (currentLine) {
            lines.push(currentLine);
          }

          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });

      if (currentLine) {
        lines.push(currentLine);
      }

      return lines;
    };

    // ==========================================
    // CALCULATE RECEIPT HEIGHT
    // ==========================================

    const issueLines = wrapRepairText(issue, 31);

    const notesLines = notes ? wrapRepairText(notes, 31) : [];

    const addressLines = address ? wrapRepairText(address, 31) : [];

    const estimatedHeight = Math.max(
      125,
      105 +
        issueLines.length * 4 +
        notesLines.length * 4 +
        addressLines.length * 3.5,
    );

    // ==========================================
    // PDF
    // ==========================================

    const doc = new jsPDF({
      orientation: "portrait",

      unit: "mm",

      format: [80, estimatedHeight],
    });

    const leftX = 6;
    const rightX = 68;
    const centerX = 39;

    let y = 7;

    const drawLine = () => {
      doc.setDrawColor(0);

      doc.setLineWidth(0.2);

      doc.line(leftX, y, rightX, y);

      y += 4;
    };

    const printRow = (label, value) => {
      doc.setFont("helvetica", "bold");

      doc.setFontSize(8.5);

      doc.text(String(label), leftX, y);

      doc.setFont("helvetica", "normal");

      doc.text(String(value ?? "-"), rightX, y, {
        align: "right",
      });

      y += 4.5;
    };

    // ==========================================
    // SHOP HEADER
    // ==========================================

    doc.setFont("courier", "bold");

    doc.setFontSize(14);

    doc.text(String(shopName), centerX, y, {
      align: "center",
    });

    y += 5;

    doc.setFont("helvetica", "normal");

    doc.setFontSize(8.5);

    addressLines.forEach((line) => {
      doc.text(line, centerX, y, {
        align: "center",
      });

      y += 3.5;
    });

    if (phone) {
      doc.text(`Tel: ${phone}`, centerX, y, {
        align: "center",
      });

      y += 3.5;
    }

    y += 2;

    drawLine();

    // ==========================================
    // TITLE
    // ==========================================

    doc.setFont("helvetica", "bold");

    doc.setFontSize(12);

    doc.text("REPAIR RECEIPT", centerX, y, {
      align: "center",
    });

    y += 5;

    doc.setFontSize(15);

    doc.text(repairCode, centerX, y, {
      align: "center",
    });

    y += 5;

    drawLine();

    // ==========================================
    // CUSTOMER
    // ==========================================

    printRow("Customer:", customerName);

    printRow("Phone:", customerPhone);

    printRow("Date:", formattedDate);

    printRow("Received By:", receivedBy);

    drawLine();

    // ==========================================
    // DEVICE
    // ==========================================

    doc.setFont("helvetica", "bold");

    doc.setFontSize(9);

    doc.text("DEVICE DETAILS", leftX, y);

    y += 5;

    printRow("Device:", deviceName);

    if (imei && imei !== "-") {
      doc.setFont("helvetica", "bold");

      doc.setFontSize(8.5);

      doc.text("IMEI / Serial:", leftX, y);

      y += 4;

      doc.setFont("helvetica", "normal");

      doc.setFontSize(8);

      const imeiLines = wrapRepairText(imei, 32);

      imeiLines.forEach((line) => {
        doc.text(line, leftX, y);

        y += 3.8;
      });
    }

    y += 1;

    // ==========================================
    // ISSUE
    // ==========================================

    doc.setFont("helvetica", "bold");

    doc.setFontSize(8.5);

    doc.text("Issue:", leftX, y);

    y += 4;

    doc.setFont("helvetica", "normal");

    doc.setFontSize(8.5);

    issueLines.forEach((line) => {
      doc.text(line, leftX, y);

      y += 4;
    });

    if (notesLines.length > 0) {
      y += 1;

      doc.setFont("helvetica", "bold");

      doc.text("Notes:", leftX, y);

      y += 4;

      doc.setFont("helvetica", "normal");

      notesLines.forEach((line) => {
        doc.text(line, leftX, y);

        y += 4;
      });
    }

    y += 1;

    drawLine();

    // ==========================================
    // PAYMENT
    // ==========================================

    doc.setFont("helvetica", "bold");

    doc.setFontSize(9);

    doc.text("PAYMENT", leftX, y);

    y += 5;

    printRow("Repair Price:", `€${totalAmount.toFixed(2)}`);

    printRow("Amount Paid:", `€${amountPaid.toFixed(2)}`);

    doc.setFont("helvetica", "bold");

    doc.setFontSize(10);

    doc.text("Balance Due:", leftX, y);

    doc.text(`€${balance.toFixed(2)}`, rightX, y, {
      align: "right",
    });

    y += 5;

    drawLine();

    // ==========================================
    // STATUS
    // ==========================================

    doc.setFont("helvetica", "bold");

    doc.setFontSize(9);

    doc.text(
  "Status:",
  leftX,
  y
);

const currentStatus =
  formatStatus(
    repair.status || "received"
  ).toUpperCase();

doc.text(
  currentStatus,
  rightX,
  y,
  {
    align: "right",
  }
);

    y += 5;

    drawLine();

    // ==========================================
    // COLLECTION MESSAGE
    // ==========================================

    doc.setFont("helvetica", "bold");

    doc.setFontSize(9);

    doc.text("Please keep this receipt.", centerX, y, {
      align: "center",
    });

    y += 4;

    doc.setFont("helvetica", "normal");

    doc.setFontSize(8.5);

    doc.text("Quote your repair number", centerX, y, {
      align: "center",
    });

    y += 3.5;

    doc.text("when collecting your device.", centerX, y, {
      align: "center",
    });

    y += 5;

    doc.setFont("helvetica", "bold");

    doc.setFontSize(11);

    doc.text(repairCode, centerX, y, {
      align: "center",
    });

    // ==========================================
    // PRINT
    // ==========================================

    const pdfDataUrl = doc.output("datauristring");

    console.log("Printing repair receipt:", {
      repairCode,
      printerName,
    });

    if (window.electron?.ipcRenderer) {
      /*
      EXACT SAME IPC as normal
      DeskPOS sale receipts.
    */
      window.electron.ipcRenderer.send(
        "print-receipt",
        pdfDataUrl,
        printerName,
      );
    } else {
      /*
      Browser development preview.
    */
      const pdfBlob = doc.output("blob");

      const pdfUrl = URL.createObjectURL(pdfBlob);

      window.open(pdfUrl, "_blank");

      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 60000);
    }
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="repairs-page">
      <header className="repairs-topbar">
        <div>
          <span className="repairs-eyebrow">DeskPOS</span>

          <h1>Repair Management</h1>
        </div>

        <button
          type="button"
          className="repairs-back-btn"
          onClick={() => navigate("/")}
        >
          Back to POS
        </button>
      </header>

      {message && <div className="repairs-message">{message}</div>}

      {error && <div className="repairs-error">{error}</div>}

      {/* ========================================= */}
      {/* SEARCH + NEW REPAIR */}
      {/* ========================================= */}

      <section className="repairs-actions-card">
        <form className="repairs-search-form" onSubmit={handleSearchSubmit}>
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Scan repair barcode or search name, phone, IMEI..."
          />

          <button type="submit" disabled={loading}>
            Search
          </button>
        </form>

        <button
          type="button"
          className="repairs-new-btn"
          onClick={() => {
            setError("");
            setMessage("");
            setForm(EMPTY_FORM);
            setShowNewRepair(true);
          }}
        >
          + New Repair
        </button>
      </section>

      {/* ========================================= */}
      {/* STATUS SUMMARY */}
      {/* ========================================= */}

      <section className="repair-status-grid">
        <button type="button">
          <span>Received</span>

          <strong>{statusCounts.received || 0}</strong>
        </button>

        <button type="button">
          <span>Diagnosing</span>

          <strong>{statusCounts.diagnosing || 0}</strong>
        </button>

        <button type="button">
          <span>Parts Ordered</span>

          <strong>{statusCounts.parts_ordered || 0}</strong>
        </button>

        <button type="button">
          <span>In Progress</span>

          <strong>{statusCounts.repair_in_progress || 0}</strong>
        </button>

        <button type="button">
          <span>Ready</span>

          <strong>{statusCounts.ready_for_collection || 0}</strong>
        </button>
      </section>

      {/* ========================================= */}
      {/* REPAIR LIST */}
      {/* ========================================= */}

      <section className="repairs-list-card">
        <div className="repairs-list-header">
          <div>
            <span>Devices</span>

            <h2>Current Repairs</h2>
          </div>

          <strong>{repairs.length}</strong>
        </div>

        {loading ? (
          <div className="repairs-empty">Loading repairs...</div>
        ) : repairs.length === 0 ? (
          <div className="repairs-empty">No repairs found.</div>
        ) : (
          <div className="repairs-list">
            {repairs.map((repair) => (
              <button
                key={repair.id}
                type="button"
                className="repair-list-item"
                onClick={() => openRepair(repair.repair_code)}
              >
                <div className="repair-list-main">
                  <strong>{repair.repair_code}</strong>

                  <span>{repair.customer_name}</span>

                  <small>{repair.device_name}</small>
                </div>

                <div className="repair-list-side">
                  <span
                    className={`repair-status-badge status-${repair.status}`}
                  >
                    {formatStatus(repair.status)}
                  </span>

                  <strong>{formatMoney(repair.balance)} due</strong>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ========================================= */}
      {/* NEW REPAIR MODAL */}
      {/* ========================================= */}

      {showNewRepair && (
        <div className="repair-modal-backdrop">
          <div className="repair-modal">
            <div className="repair-modal-header">
              <div>
                <span>New Job</span>

                <h2>Create Repair</h2>
              </div>

              <button type="button" onClick={() => setShowNewRepair(false)}>
                ×
              </button>
            </div>

            <form className="repair-form" onSubmit={createRepair}>
              <label>
                Customer Name *
                <input
                  type="text"
                  value={form.customer_name}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      customer_name: event.target.value,
                    })
                  }
                  required
                />
              </label>

              <label>
                Customer Phone
                <input
                  type="text"
                  value={form.customer_phone}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      customer_phone: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Device / Model *
                <input
                  type="text"
                  value={form.device_name}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      device_name: event.target.value,
                    })
                  }
                  placeholder="Example: iPhone 14"
                  required
                />
              </label>

              <label>
                IMEI / Serial
                <input
                  type="text"
                  value={form.imei}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      imei: event.target.value,
                    })
                  }
                />
              </label>

              <label className="repair-form-full">
                Issue
                <textarea
                  value={form.issue}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      issue: event.target.value,
                    })
                  }
                  placeholder="Describe the repair..."
                />
              </label>

              <label>
                Repair Price
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.total_amount}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      total_amount: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Amount Paid
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount_paid}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      amount_paid: event.target.value,
                    })
                  }
                />
              </label>

              <label className="repair-form-full">
                Notes
                <textarea
                  value={form.notes}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      notes: event.target.value,
                    })
                  }
                />
              </label>

              <div className="repair-form-actions repair-form-full">
                <button
                  type="button"
                  className="repair-cancel-btn"
                  onClick={() => setShowNewRepair(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="repair-save-btn"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Create Repair"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* REPAIR DETAILS MODAL */}
      {/* ========================================= */}

      {selectedRepair && (
        <div className="repair-modal-backdrop">
          <div className="repair-modal repair-details-modal">
            {/* HEADER */}

            <div className="repair-modal-header">
              <div>
                <span>{selectedRepair.repair_code}</span>

                <h2>{selectedRepair.device_name}</h2>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedRepair(null);
                  setRepairHistory([]);

                  setTimeout(() => {
                    searchRef.current?.focus();
                  }, 100);
                }}
              >
                ×
              </button>
            </div>

            {/* ================================= */}
            {/* DEVICE / CUSTOMER INFO */}
            {/* ================================= */}

            <div className="repair-detail-grid">
              <div>
                <span>Customer</span>

                <strong>{selectedRepair.customer_name}</strong>
              </div>

              <div>
                <span>Phone</span>

                <strong>{selectedRepair.customer_phone || "-"}</strong>
              </div>

              <div>
                <span>IMEI</span>

                <strong>{selectedRepair.imei || "-"}</strong>
              </div>

              <div>
                <span>Received By</span>

                <strong>{selectedRepair.received_by_name || "-"}</strong>
              </div>
            </div>

            {/* ================================= */}
            {/* ISSUE */}
            {/* ================================= */}

            <div className="repair-issue-box">
              <span>Issue</span>

              <strong>{selectedRepair.issue || "No issue recorded"}</strong>
            </div>

            {/* ================================= */}
            {/* PAYMENT SUMMARY */}
            {/* ================================= */}

            <div className="repair-payment-grid">
              <div>
                <span>Total</span>

                <strong>{formatMoney(selectedRepair.total_amount)}</strong>
              </div>

              <div>
                <span>Paid</span>

                <strong>{formatMoney(selectedRepair.amount_paid)}</strong>
              </div>

              <div>
                <span>Balance</span>

                <strong>{formatMoney(selectedRepair.balance)}</strong>
              </div>
            </div>

            {/* ================================= */}
            {/* ACTION BUTTONS */}
            {/* ================================= */}

            <div className="repair-detail-actions">
              <button
                type="button"
                className="repair-payment-btn"
                onClick={openPaymentModal}
                disabled={saving || selectedRepair.status === "collected"}
                title={
                  selectedRepair.status === "collected"
                    ? "Payment cannot be changed after the repair has been collected"
                    : ""
                }
              >
                {selectedRepair.status === "collected"
                  ? "Payment Locked"
                  : "Update Payment"}
              </button>

              <button
                type="button"
                className="repair-print-btn"
                onClick={() => printRepairReceipt(selectedRepair)}
              >
                Print Repair Receipt
              </button>
            </div>

            {/* ================================= */}
            {/* STATUS */}
            {/* ================================= */}

            <div className="repair-status-section">
              <h3>Current Status</h3>

              <span
                className={`repair-status-large status-${selectedRepair.status}`}
              >
                {formatStatus(selectedRepair.status)}
              </span>

              <div className="repair-status-buttons">
                {STATUS_OPTIONS.map((status) => (
                  <button
                    key={status.value}
                    type="button"
                    disabled={saving || status.value === selectedRepair.status}
                    onClick={() => changeStatus(status.value)}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ================================= */}
            {/* HISTORY */}
            {/* ================================= */}

            <div className="repair-history-section">
              <h3>Repair History</h3>

              {repairHistory.length === 0 ? (
                <p>No history available.</p>
              ) : (
                repairHistory.map((history) => (
                  <div key={history.id} className="repair-history-item">
                    <strong>{formatStatus(history.new_status)}</strong>

                    <span>{history.user_name || "Staff"}</span>

                    <small>
                      {history.created_at
                        ? new Date(history.created_at).toLocaleString()
                        : ""}
                    </small>

                    {history.note && <p>{history.note}</p>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* UPDATE PAYMENT MODAL */}
      {/* ========================================= */}

      {showPaymentModal && selectedRepair && (
        <div className="repair-payment-overlay">
          <div className="repair-payment-modal">
            {/* HEADER */}

            <div className="repair-payment-modal-header">
              <div>
                <span>{selectedRepair.repair_code}</span>

                <h2>Update Payment</h2>
              </div>

              <button
                type="button"
                className="repair-payment-close"
                disabled={saving}
                onClick={() => setShowPaymentModal(false)}
              >
                ×
              </button>
            </div>

            {/* CUSTOMER / DEVICE */}

            <div className="repair-payment-device">
              <span>{selectedRepair.customer_name}</span>

              <strong>{selectedRepair.device_name}</strong>
            </div>

            {/* PAYMENT INPUTS */}

            <div className="repair-payment-modal-body">
              <label>
                Repair Total
                <div className="repair-money-input">
                  <span>€</span>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentTotal}
                    disabled={saving}
                    onChange={(event) => setPaymentTotal(event.target.value)}
                  />
                </div>
              </label>

              <label>
                Amount Paid
                <div className="repair-money-input">
                  <span>€</span>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentPaid}
                    disabled={saving}
                    onChange={(event) => setPaymentPaid(event.target.value)}
                  />
                </div>
              </label>

              {/* BALANCE */}

              <div className="repair-payment-balance-preview">
                <span>Balance After Payment</span>

                <strong>
                  {formatMoney(
                    Math.max(
                      0,
                      Number(paymentTotal || 0) - Number(paymentPaid || 0),
                    ),
                  )}
                </strong>
              </div>
            </div>

            {/* ACTIONS */}

            <div className="repair-payment-modal-actions">
              <button
                type="button"
                className="repair-payment-cancel"
                disabled={saving}
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="repair-payment-save"
                disabled={saving}
                onClick={savePaymentUpdate}
              >
                {saving ? "Saving..." : "Save Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RepairsPage;
